import React, { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  ButtonGroup,
  Banner,
  Badge,
  Text,
  TextField,
  InlineStack,
  BlockStack,
  Spinner,
  Tooltip,
  Box,
  Divider,
  Checkbox,
  Select,
} from '@shopify/polaris';
import { PolarisVizProvider, StackedAreaChart, TooltipContent } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';
import { authenticatedFetch } from './AuthComponent';

const API = '/api/sales-velocity';
const WINDOWS = [7, 30, 90];
const SERIES_COLORS = { lightspeed: '#2E7D32', ecommerce: '#1E88E5', amazon: '#F57C00' };

async function getJSON(url) {
  const r = await authenticatedFetch(url);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.detail || `Request failed (${r.status})`);
  return body;
}

function formatDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonth(ts) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatSynced(ts) {
  if (!ts) return 'never';
  return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Units per week over the report window. The window includes today, which is
// still in progress, so the newest numbers run slightly low.
function perWeek(units, days) {
  return days ? (units / days) * 7 : 0;
}

function formatRate(rate) {
  if (!rate) return '0';
  return rate < 10 ? rate.toFixed(1) : String(Math.round(rate));
}

// The breakdown beside the chart: one list per channel that splits by location.
// Lightspeed splits by store; E-commerce and Amazon by the website the order came
// through (Amazon orders reach Shopify through Soccer Post's and City Sports' stores).
const BREAKDOWN_LABELS = { lightspeed: 'Stores', ecommerce: 'Websites', amazon: 'Amazon' };
const BREAKDOWN_NOUNS = { lightspeed: 'stores', ecommerce: 'websites', amazon: 'websites' };

function Breakdown({ channels, report, chartRow }) {
  const [picked, setPicked] = useState(null);
  const channel = channels.find((c) => c.key === picked) || channels[0];
  const source = chartRow ? (report.stores_by_sku[chartRow.sku] || {}) : report.stores;
  const list = source[channel.key] || [];
  const max = Math.max(1, ...list.map((s) => s.units));
  const total = list.reduce((n, s) => n + s.units, 0);
  const scope = chartRow ? `Size ${chartRow.variant_title || chartRow.sku}` : 'All sizes';
  const noun = BREAKDOWN_NOUNS[channel.key] || 'locations';
  return (
    <div className="uh-sv-side">
      <InlineStack align="space-between" blockAlign="center" gap="200">
        <Text variant="headingMd" as="h3">
          {channels.length > 1 ? 'Breakdown' : `${channel.label} by ${noun === 'stores' ? 'store' : 'website'}`}
        </Text>
        {channels.length > 1 && (
          <ButtonGroup variant="segmented">
            {channels.map((c) => (
              <Button key={c.key} size="slim" pressed={c.key === channel.key} onClick={() => setPicked(c.key)}>
                {BREAKDOWN_LABELS[c.key] || c.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </InlineStack>
      <Text tone="subdued" variant="bodySm">
        {`${channels.length > 1 ? `${channel.label} · ` : ''}${scope} · ${list.length} ${noun} · ${total} units`}
      </Text>
      {list.length ? (
        <ul className="uh-sv-bars">
          {list.map((s) => (
            <li key={s.store}>
              <span className="nm" title={s.store}>{s.store}</span>
              <span className="bar"><span style={{ width: `${(Math.max(0, s.units) / max) * 100}%`, background: SERIES_COLORS[channel.key] }} /></span>
              <span className="n">{s.units}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Text tone="subdued">{`No ${channel.label} sales in this window.`}</Text>
      )}
    </div>
  );
}

function Report({ report, visibleChannels, onShowStyle }) {
  const [chartSku, setChartSku] = useState(null);   // null = all sizes
  const channels = report.channels.filter((c) => visibleChannels.includes(c.key));
  const sumVisible = (units) => channels.reduce((n, c) => n + (units[c.key] || 0), 0);

  // A size picked before changing the window stays picked if it's still in the table.
  const chartRow = report.rows.find((r) => r.sku === chartSku) || null;
  const canPickSize = report.rows.length > 1;
  // The breakdown lists every channel that splits by location, whatever the
  // platform checkboxes say: it is the only place those splits show.
  const breakdownChannels = report.channels.filter((c) => c.has_stores && c.connected);

  // Units per week over each window, side by side, whichever window the table shows.
  const windowRate = (units, w) => formatRate(perWeek(sumVisible(units[String(w)] || {}), w));
  const variantCell = (r) => {
    const label = r.variant_title || r.sku;
    if (!canPickSize) return label;
    const picked = chartRow && chartRow.sku === r.sku;
    return (
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Button variant="plain" onClick={() => setChartSku(picked ? null : r.sku)}>{label}</Button>
        {picked && <Badge tone="info">Charted</Badge>}
      </InlineStack>
    );
  };
  const sizeOptions = [{ label: 'All sizes', value: '' }]
    .concat(report.rows.map((r) => ({ label: r.variant_title || r.sku, value: r.sku })));

  const daily = chartRow ? report.daily_by_sku[chartRow.sku] : report.daily;
  // Stacked, so the top edge of the chart is the total of the checked platforms.
  const chartData = channels.map((c) => ({
    name: c.label,
    color: SERIES_COLORS[c.key],
    data: daily.map((d) => ({ key: formatDay(d.date), value: d[c.key] })),
  }));
  // The hover tooltip lists each platform; add the day's total when there's more than one.
  // Values must be strings: TooltipContent iterates them to measure width, and a
  // number throws, which the chart swallows so no tooltip shows at all.
  const renderTooltip = ({ data, activeIndex, title, theme }) => {
    const points = data[0].data.map((p) => ({ ...p, value: String(p.value) }));
    if (channels.length > 1) points.push({ key: 'Total', value: String(sumVisible(daily[activeIndex])) });
    return <TooltipContent data={[{ ...data[0], data: points }]} title={title} theme={theme} />;
  };

  const windowLabel = `${formatDay(report.start)} – ${formatDay(report.end)}`;

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="start">
            <BlockStack gap="100">
              <Text variant="headingLg" as="h2">{report.product_title}</Text>
              <Text tone="subdued">
                {[report.vendor, report.style_code && `Style ${report.style_code}`].filter(Boolean).join(' · ')}
              </Text>
            </BlockStack>
            <BlockStack gap="100" inlineAlign="end">
              <Text tone="subdued">{`${windowLabel} (${report.days} days)`}</Text>
              {report.rows.length === 1 && onShowStyle && (
                <Button variant="plain" onClick={onShowStyle}>Show all sizes of this style</Button>
              )}
            </BlockStack>
          </InlineStack>
          <InlineStack gap="600" wrap>
            {WINDOWS.map((w) => (
              <BlockStack gap="050" key={w}>
                <Text tone="subdued" variant="bodySm">{`Last ${w} days`}</Text>
                <Text variant="headingMd" as="p">{`${windowRate(report.window_totals, w)} / week`}</Text>
              </BlockStack>
            ))}
          </InlineStack>
          {canPickSize && (
            <Text tone="subdued" variant="bodySm">Click a size to chart it, or pick one above the chart.</Text>
          )}
          {/* Units follow the selected window; the rates always cover 7, 30 and 90 days.
              Column groups, dividers and the tint on the rates keep the two apart. */}
          <div className="uh-tscroll">
            <table className="uh-tbl uh-sv-tbl">
              <thead>
                <tr className="grp">
                  <th colSpan={2} aria-hidden="true" />
                  <th colSpan={channels.length + 1} scope="colgroup" className="div">{`Units sold · ${windowLabel}`}</th>
                  <th colSpan={WINDOWS.length} scope="colgroup" className="div rate">
                    <Tooltip content="Average units sold per week over each window (units ÷ days × 7). Doesn't change with the window buttons.">
                      <span>Avg units per week</span>
                    </Tooltip>
                  </th>
                </tr>
                <tr>
                  <th>Variant</th>
                  <th>SKU</th>
                  {channels.map((c, i) => <th key={c.key} className={`r${i === 0 ? ' div' : ''}`}>{c.label}</th>)}
                  <th className={`r${channels.length ? '' : ' div'}`}>Total</th>
                  {WINDOWS.map((w, i) => <th key={w} className={`r rate${i === 0 ? ' div' : ''}`}>{`${w} days`}</th>)}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.sku} className={chartRow && chartRow.sku === r.sku ? 'sel' : undefined}>
                    <td>{variantCell(r)}</td>
                    <td className="sm">{r.sku}</td>
                    {channels.map((c, i) => <td key={c.key} className={`r${i === 0 ? ' div' : ''}`}>{r.units[c.key]}</td>)}
                    <td className={`r nm${channels.length ? '' : ' div'}`}>{sumVisible(r.units)}</td>
                    {WINDOWS.map((w, i) => (
                      <td key={w} className={`r rate${i === 0 ? ' div' : ''}`}>{windowRate(r.window_units, w)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="nm">Total</td>
                  <td />
                  {channels.map((c, i) => <td key={c.key} className={`r nm${i === 0 ? ' div' : ''}`}>{report.totals[c.key]}</td>)}
                  <td className={`r nm${channels.length ? '' : ' div'}`}>{sumVisible(report.totals)}</td>
                  {WINDOWS.map((w, i) => (
                    <td key={w} className={`r nm rate${i === 0 ? ' div' : ''}`}>{windowRate(report.window_totals, w)}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </BlockStack>
      </Card>

      <Card>
        <div className="uh-sv-chartrow">
          <div className="uh-sv-chart">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" gap="200">
                <Text variant="headingMd" as="h3">Units sold per day</Text>
                {canPickSize && (
                  <Select
                    label="Size"
                    labelInline
                    options={sizeOptions}
                    value={chartRow ? chartRow.sku : ''}
                    onChange={(v) => setChartSku(v || null)}
                  />
                )}
              </InlineStack>
              <div style={{ height: 320 }}>
                <PolarisVizProvider>
                  <StackedAreaChart
                    data={chartData}
                    theme="Light"
                    tooltipOptions={{ renderTooltipContent: renderTooltip }}
                  />
                </PolarisVizProvider>
              </div>
            </BlockStack>
          </div>
          {breakdownChannels.length > 0 && (
            <Breakdown channels={breakdownChannels} report={report} chartRow={chartRow} />
          )}
        </div>
      </Card>
    </BlockStack>
  );
}

export default function SalesVelocity() {
  const [query, setQuery] = useState('');
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);     // {sku, groups}
  const [selection, setSelection] = useState(null); // {sku} | {group}
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibleChannels, setVisibleChannels] = useState(null); // null until status loads
  const [visibleWebsites, setVisibleWebsites] = useState(null); // null until status loads

  useEffect(() => {
    getJSON(`${API}/status`).then(setStatus).catch(() => {});
  }, []);

  // Default: every connected platform on.
  useEffect(() => {
    if (status && visibleChannels === null) {
      setVisibleChannels(status.channels.filter((c) => c.connected).map((c) => c.key));
    }
  }, [status, visibleChannels]);

  // Default: every website on. Unlike the platform checkboxes, these change
  // what the server adds up, so the report is fetched again.
  useEffect(() => {
    if (status?.websites && visibleWebsites === null) {
      setVisibleWebsites(status.websites.map((w) => w.key));
    }
  }, [status, visibleWebsites]);

  const toggleChannel = (key, on) => {
    setVisibleChannels((cur) => (on ? [...cur, key] : cur.filter((k) => k !== key)));
  };

  const loadReport = useCallback(async (sel, windowDays, websites) => {
    setSelection(sel);
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ days: String(windowDays), ...sel });
      // Left out when every website is ticked, so a new website is included by default.
      if (websites && status?.websites && websites.length < status.websites.length) {
        params.set('websites', websites.join(','));
      }
      const body = await getJSON(`${API}/report?${params}`);
      setReport(body);
      setStatus(body.status);
    } catch (e) {
      setError(e.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [status]);

  const toggleWebsite = (key, on) => {
    const next = on ? [...visibleWebsites, key] : visibleWebsites.filter((k) => k !== key);
    setVisibleWebsites(next);
    if (selection) loadReport(selection, days, next);
  };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) {
      setError('Enter at least 2 characters.');
      return;
    }
    setLoading(true);
    setError('');
    setReport(null);
    setResults(null);
    setSelection(null);
    setPickerOpen(true);
    try {
      const body = await getJSON(`${API}/search?${new URLSearchParams({ q })}`);
      setResults(body);
      if (body.sku) {
        await loadReport({ sku: body.sku.sku }, days, visibleWebsites);
      } else if (body.groups.length === 1) {
        await loadReport({ group: body.groups[0].group_key }, days, visibleWebsites);
      } else if (!body.groups.length) {
        setError(`Nothing matches “${q}”.`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const changeWindow = (d) => {
    setDays(d);
    if (selection) loadReport(selection, d, visibleWebsites);
  };

  const [pickerOpen, setPickerOpen] = useState(true);
  const multipleMatches = results && !results.sku && results.groups.length > 1;
  const showPicker = multipleMatches && pickerOpen;
  // Shopify (orders + catalog, every website) syncs hourly; Lightspeed sales come
  // from the Inventory rebalancing syncs, a few times a day. Show each one's time,
  // the oldest website's for Shopify, so one store falling behind is visible.
  const sources = status?.sources || [];
  const oldest = (list) => list.reduce((a, s) => (a && a < s.last_success_at ? a : s.last_success_at), null);
  const shopifySync = oldest(sources.filter((s) => s.source !== 'lightspeed'));
  const lightspeedSource = sources.find((s) => s.source === 'lightspeed');
  const syncLine = [
    `Shopify synced ${formatSynced(shopifySync)}`,
    lightspeedSource && `Lightspeed synced ${formatSynced(lightspeedSource.last_success_at)}`,
  ].filter(Boolean).join(' · ');

  return (
    <Page
      title="Sales Velocity"
      subtitle="Units sold per channel for a SKU or style"
    >
      <Layout>
        {status?.sample_mode && (
          <Layout.Section>
            <Banner tone="warning" title="Sample data">
              <p>Every sales number here is invented for testing. Real Shopify data replaces it once the sync is connected.</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <div onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}>
                <TextField
                  label="SKU, style, or product name"
                  value={query}
                  onChange={setQuery}
                  placeholder="e.g. ADID-HD6898-XL, HD6898, or Mexico home jersey"
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setQuery('')}
                  connectedRight={<Button variant="primary" onClick={runSearch} loading={loading && !report}>Search</Button>}
                />
              </div>
              <InlineStack align="space-between" blockAlign="center">
                <ButtonGroup variant="segmented">
                  {WINDOWS.map((w) => (
                    <Button key={w} pressed={days === w} onClick={() => changeWindow(w)}>
                      {`Last ${w} days`}
                    </Button>
                  ))}
                </ButtonGroup>
                <Text tone="subdued" variant="bodySm">
                  {status?.sample_mode ? 'Sample data' : syncLine}
                </Text>
              </InlineStack>
              {status && visibleChannels && (
                <InlineStack gap="400" blockAlign="center">
                  <Text variant="bodySm" fontWeight="semibold">Platforms</Text>
                  {status.channels.map((c) => {
                    const checked = c.connected && visibleChannels.includes(c.key);
                    const onlyOneLeft = checked && visibleChannels.length === 1;
                    return (
                      <Checkbox
                        key={c.key}
                        label={c.connected ? c.label : `${c.label} (not connected yet)`}
                        checked={checked}
                        disabled={!c.connected || onlyOneLeft}
                        onChange={(on) => toggleChannel(c.key, on)}
                      />
                    );
                  })}
                </InlineStack>
              )}
              {status?.websites && visibleWebsites && (
                <InlineStack gap="400" blockAlign="center">
                  <Tooltip content="E-commerce and Amazon orders from these Shopify stores. Lightspeed isn't affected.">
                    <Text variant="bodySm" fontWeight="semibold">Websites</Text>
                  </Tooltip>
                  {status.websites.map((w) => {
                    const checked = visibleWebsites.includes(w.key);
                    return (
                      <Checkbox
                        key={w.key}
                        label={w.label}
                        checked={checked}
                        disabled={checked && visibleWebsites.length === 1}
                        onChange={(on) => toggleWebsite(w.key, on)}
                      />
                    );
                  })}
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}><p>{error}</p></Banner>
          </Layout.Section>
        )}

        {showPicker && (
          <Layout.Section>
            <Card padding="0">
              <Box padding="400">
                <Text variant="headingMd" as="h3">{`${results.groups.length} matching styles${results.groups.length === 25 ? ' (top 25: best sellers and newest first — refine your search)' : ''}`}</Text>
              </Box>
              {results.groups.map((g, i) => (
                <React.Fragment key={g.group_key}>
                  {i > 0 && <Divider />}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { setPickerOpen(false); loadReport({ group: g.group_key }, days, visibleWebsites); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setPickerOpen(false); loadReport({ group: g.group_key }, days, visibleWebsites); } }}
                    style={{
                      cursor: 'pointer',
                      background: selection?.group === g.group_key ? 'var(--p-color-bg-surface-selected)' : undefined,
                    }}
                  >
                    <Box padding="300" paddingInlineStart="400" paddingInlineEnd="400">
                      <InlineStack align="space-between" blockAlign="center" wrap={false} gap="300">
                        <BlockStack gap="050">
                          <Text fontWeight="semibold">{g.product_title}</Text>
                          <Text tone="subdued" variant="bodySm">
                            {[
                              g.vendor,
                              g.style_code,
                              `${g.sku_count} SKU${g.sku_count === 1 ? '' : 's'}`,
                              `${g.units_90d ?? 0} sold in 90 days`,
                              g.created_at && `added ${formatMonth(g.created_at)}`,
                            ].filter(Boolean).join(' · ')}
                          </Text>
                        </BlockStack>
                        {g.status && g.status !== 'ACTIVE' && <Badge>{g.status.toLowerCase()}</Badge>}
                      </InlineStack>
                    </Box>
                  </div>
                </React.Fragment>
              ))}
            </Card>
          </Layout.Section>
        )}

        {multipleMatches && !pickerOpen && (
          <Layout.Section>
            <InlineStack gap="200" blockAlign="center">
              <Text tone="subdued">{`${results.groups.length} styles matched “${query.trim()}”.`}</Text>
              <Button variant="plain" onClick={() => setPickerOpen(true)}>Choose a different style</Button>
            </InlineStack>
          </Layout.Section>
        )}

        {loading && !report && selection && (
          <Layout.Section>
            <InlineStack align="center"><Spinner accessibilityLabel="Loading report" /></InlineStack>
          </Layout.Section>
        )}

        {report && (
          <Layout.Section>
            <Report
              key={report.group_key}
              report={report}
              visibleChannels={visibleChannels || []}
              onShowStyle={selection?.sku ? () => loadReport({ group: report.group_key }, days, visibleWebsites) : null}
            />
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
